import { useReactFlow } from '@xyflow/react';
import { useMemo, useState, useEffect, ReactNode, useCallback } from 'react';
import { appConfig, APP_VERSION, defaultProjectData } from 'src/config';
import { AppNode } from 'src/modules/network-configuration/models/node-model';
import { calculateEdgeAnimated } from 'src/modules/network-configuration/models/node-utils';
import { ProjectJsonData } from 'src/types/project';
import { sanitizePoEDevicesConnectedToNonPoEPorts } from 'src/utils/sanitize-poe';

import { useGlobalContext } from '../global/use-global-context';
import { UseUndoRedoOptions, UndoRedoContextType } from './types';
import { UndoRedoContext } from './undo-redo-context';

const defaultOptions: UseUndoRedoOptions = {
  maxHistorySize: 100,
  enableShortcuts: true,
  enableLocalStorage: true,
};

interface UndoRedoProviderProps {
  children: ReactNode;
  options?: Partial<UseUndoRedoOptions>;
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    const { type } = target;
    return (
      type === 'text' ||
      type === 'search' ||
      type === 'email' ||
      type === 'password' ||
      type === 'url' ||
      type === 'tel' ||
      type === 'number'
    );
  }
  return false;
}

const defaultHistory: ProjectJsonData = {
  version: APP_VERSION,
  nodes: [],
  edges: [],
  vlans: [],
  globalIpSubnet: undefined,
  useCustomVlan: false,
  projectData: defaultProjectData,
  lastChanged: '',
  globalSettings: undefined,
};

const loadHistoryFromLocalStorage = (storageKey: string): ProjectJsonData[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate the structure
      if (
        Array.isArray(parsed) &&
        parsed.every(
          (item) =>
            item.nodes && item.edges && typeof item.timestamp === 'number',
        )
      ) {
        return parsed;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load undo/redo history from localStorage:', error);
  }
  return [];
};

const saveHistoryToLocalStorage = (
  storageKey: string,
  history: ProjectJsonData[],
): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(history));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save undo/redo history to localStorage:', error);
  }
};

const saveCurrentStateToLocalStorage = (
  storageKey: string,
  currentState: ProjectJsonData,
): void => {
  localStorage.setItem(storageKey, JSON.stringify(currentState));
};

const loadCurrentStateFromLocalStorage = (
  storageKey: string,
): ProjectJsonData => {
  const stored = localStorage.getItem(storageKey);
  if (stored) {
    return JSON.parse(stored);
  }
  return defaultHistory;
};

export const UndoRedoProvider = ({
  children,
  options = {},
}: UndoRedoProviderProps) => {
  const {
    maxHistorySize = defaultOptions.maxHistorySize,
    enableShortcuts = defaultOptions.enableShortcuts,
    enableLocalStorage = defaultOptions.enableLocalStorage,
  } = options;
  const {
    vlans,
    setVlans,
    globalIpSubnet,
    useCustomVlan,
    setGlobalIpSubnet,
    setUseCustomVlan,
    projectData,
    setProjectData,
    lastChanged,
    setLastChanged,
    globalSettings,
    setGlobalSettings,
  } = useGlobalContext();
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow();

  const [initialized, setInitialized] = useState(false);
  const [, setForceUpdate] = useState(0);

  const [past, setPast] = useState<ProjectJsonData[]>(() => {
    if (enableLocalStorage) {
      return loadHistoryFromLocalStorage(appConfig.undoRedoStorageKey);
    }
    return [];
  });

  const [future, setFuture] = useState<ProjectJsonData[]>([]);

  const nodes = getNodes();

  const applyData = useCallback(
    (data: ProjectJsonData) => {
      const sanitizedNodes = sanitizePoEDevicesConnectedToNonPoEPorts(
        data.nodes as AppNode[],
        data.edges,
      );
      setNodes(sanitizedNodes);
      setEdges(
        data.edges.map((edge) => ({
          ...edge,
          animated: !!calculateEdgeAnimated(
            edge.source,
            edge.target,
            sanitizedNodes,
          ),
        })),
      );
      setVlans(data.vlans);
      setGlobalIpSubnet(data.globalIpSubnet);
      setUseCustomVlan(data.useCustomVlan);
      setProjectData(data.projectData);
      setLastChanged(data.lastChanged);
      setGlobalSettings(data.globalSettings);
    },
    [
      setNodes,
      setVlans,
      setEdges,
      setGlobalIpSubnet,
      setUseCustomVlan,
      setProjectData,
      setLastChanged,
      setGlobalSettings,
    ],
  );

  const takeSnapshot = useCallback(() => {
    setLastChanged(new Date().toISOString());
    const currentState: ProjectJsonData = {
      nodes: getNodes() as AppNode[],
      edges: getEdges(),
      vlans,
      globalIpSubnet,
      useCustomVlan,
      projectData: projectData || defaultProjectData,
      lastChanged,
      globalSettings,
    };

    setPast((prev) => {
      if (prev.length > 0) {
        const lastState = prev[prev.length - 1];

        const isEqual =
          JSON.stringify(currentState) === JSON.stringify(lastState);
        if (isEqual) return prev;
      }

      const newPast = [
        ...prev.slice(prev.length - maxHistorySize + 1, prev.length),
        currentState,
      ];

      if (enableLocalStorage) {
        saveHistoryToLocalStorage(appConfig.undoRedoStorageKey, newPast);
      }

      return newPast;
    });

    // Clear future when taking new snapshot
    setFuture([]);
  }, [
    getNodes,
    getEdges,
    vlans,
    globalIpSubnet,
    useCustomVlan,
    projectData,
    maxHistorySize,
    enableLocalStorage,
    lastChanged,
    setLastChanged,
    globalSettings,
  ]);

  const undo = useCallback(() => {
    const pastState = past[past.length - 1];

    if (pastState) {
      setPast((prev) => {
        const newPast = prev.slice(0, prev.length - 1);
        if (enableLocalStorage) {
          saveHistoryToLocalStorage(appConfig.undoRedoStorageKey, newPast);
        }
        return newPast;
      });

      setFuture((prev) => [
        ...prev,
        {
          nodes: getNodes() as AppNode[],
          edges: getEdges(),
          vlans,
          globalIpSubnet,
          useCustomVlan,
          projectData: projectData || defaultProjectData,
          lastChanged,
          globalSettings,
        },
      ]);

      applyData(pastState);
    }
  }, [
    past,
    applyData,
    enableLocalStorage,
    getNodes,
    getEdges,
    vlans,
    globalIpSubnet,
    useCustomVlan,
    projectData,
    lastChanged,
    globalSettings,
  ]);

  const redo = useCallback(() => {
    const futureState = future[future.length - 1];

    if (futureState) {
      setFuture((prev) => prev.slice(0, prev.length - 1));
      setPast((prev) => {
        const newPast = [
          ...prev,
          {
            nodes: getNodes() as AppNode[],
            edges: getEdges(),
            vlans,
            globalIpSubnet,
            useCustomVlan,
            projectData: projectData || defaultProjectData,
            lastChanged,
            globalSettings,
          },
        ];
        if (enableLocalStorage) {
          saveHistoryToLocalStorage(appConfig.undoRedoStorageKey, newPast);
        }
        return newPast;
      });

      applyData(futureState);
    }
  }, [
    applyData,
    enableLocalStorage,
    future,
    getEdges,
    getNodes,
    globalIpSubnet,
    projectData,
    useCustomVlan,
    vlans,
    lastChanged,
    globalSettings,
  ]);

  const clearAll = useCallback(() => {
    if (getNodes().length > 0) {
      const isConfirm = window.confirm(
        'Are you sure you want to clear all? This action cannot be undone.',
      );
      if (!isConfirm) return false;
    }

    setNodes([]);
    setEdges([]);
    setVlans([]);
    setGlobalIpSubnet(undefined);
    setUseCustomVlan(false);
    setProjectData(defaultProjectData);
    setLastChanged('');
    setGlobalSettings(undefined);
    setPast([]);
    setFuture([]);
    if (enableLocalStorage) {
      localStorage.removeItem(appConfig.undoRedoStorageKey);
      localStorage.removeItem(appConfig.currentStateStorageKey);
    }
    return true;
  }, [
    enableLocalStorage,
    setNodes,
    setEdges,
    setVlans,
    setGlobalIpSubnet,
    setUseCustomVlan,
    setProjectData,
    getNodes,
    setLastChanged,
    setGlobalSettings,
  ]);

  const canClearAll = useMemo(
    () => past.length > 0 || future.length > 0 || nodes.length > 0,
    [past.length, future.length, nodes.length],
  );

  const clearHistory = useCallback(() => {
    setPast([]);
    setFuture([]);
    if (enableLocalStorage) {
      localStorage.removeItem(appConfig.undoRedoStorageKey);
    }
  }, [enableLocalStorage]);

  useEffect(() => {
    if (!enableShortcuts) {
      return () => {};
    }

    const keyDownHandler = (event: KeyboardEvent) => {
      if (isTextEditingTarget(event.target)) return;

      if (
        event.key === 'z' &&
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey
      ) {
        event.preventDefault();
        redo();
      } else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        undo();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [undo, redo, enableShortcuts]);

  const saveCurrentState = useCallback(() => {
    if (!initialized) return;
    setForceUpdate((prev) => prev + 1);
    const data = {
      nodes: getNodes() as AppNode[],
      edges: getEdges(),
      vlans,
      globalIpSubnet,
      useCustomVlan,
      projectData: projectData || defaultProjectData,
      lastChanged,
      globalSettings,
    };

    saveCurrentStateToLocalStorage(appConfig.currentStateStorageKey, data);
  }, [
    getNodes,
    getEdges,
    initialized,
    vlans,
    globalIpSubnet,
    useCustomVlan,
    projectData,
    lastChanged,
    globalSettings,
  ]);

  useEffect(() => {
    if (initialized) return;

    const currentState = loadCurrentStateFromLocalStorage(
      appConfig.currentStateStorageKey,
    );
    if (currentState) {
      setNodes(currentState.nodes);
      setEdges(currentState.edges);
      setVlans(currentState.vlans);
      setGlobalIpSubnet(currentState.globalIpSubnet);
      setUseCustomVlan(currentState.useCustomVlan);
      setProjectData(currentState.projectData || defaultProjectData);
      setLastChanged(currentState.lastChanged);
      setGlobalSettings(currentState.globalSettings);
      setInitialized(true);
    }
  }, [
    setNodes,
    setEdges,
    setVlans,
    setGlobalIpSubnet,
    setUseCustomVlan,
    setProjectData,
    initialized,
    setLastChanged,
    setGlobalSettings,
  ]);

  const contextValue: UndoRedoContextType = useMemo(
    () => ({
      undo,
      redo,
      takeSnapshot,
      canUndo: past.length > 0,
      canRedo: future.length > 0,
      clearAll,
      clearHistory,
      historyCount: past.length,
      futureCount: future.length,
      saveCurrentState,
      canClearAll,
    }),
    [
      undo,
      redo,
      takeSnapshot,
      past.length,
      future.length,
      clearAll,
      clearHistory,
      saveCurrentState,
      canClearAll,
    ],
  );

  return (
    <UndoRedoContext.Provider value={contextValue}>
      {children}
    </UndoRedoContext.Provider>
  );
};
